import React from 'react';
import { Footprints, Users, Heart, Sparkles, Check, ExternalLink, Smartphone, Baby, GraduationCap, User, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';
import { SEO } from '../components/SEO';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

const getageGroups = () => [
  {
    label: t('pg.SocialRunClub.701296'),
    age: t('pg.SocialRunClub.18c671'),
    icon: Baby,
    url: 'https://app.hoopit.io/join/veitvet-sportsklubb-532465/open/barn-8-12-ar-social-run-club-lpegruppe-622883',
    color: 'green',
  },
  {
    label: t('pg.SocialRunClub.c82cce'),
    age: t('pg.SocialRunClub.e01e77'),
    icon: GraduationCap,
    url: 'https://app.hoopit.io/join/veitvet-sportsklubb-532465/open/ungdom-13-19-ar-social-run-club-lpegruppe-396552',
    color: 'green',
  },
  {
    label: t('pg.SocialRunClub.8c2e46'),
    age: t('pg.SocialRunClub.2abc45'),
    icon: User,
    url: 'https://app.hoopit.io/join/veitvet-sportsklubb-532465/open/voksen-social-run-club-lpegruppe-919428',
    color: 'green',
  },
];

const getbenefits = () => [
  t('pg.SocialRunClub.c4b3cb'),
  t('pg.SocialRunClub.9165c6'),
  t('pg.SocialRunClub.0dbcc9'),
  t('pg.SocialRunClub.3b28f5'),
];

const getmembershipPrices = () => [
  { type: t('pg.SocialRunClub.8c2e46'), price: 350 },
  { type: t('pg.SocialRunClub.356b7d'), price: 250 },
];

const gettrainingPrices = () => [
  { type: t('pg.SocialRunClub.8c2e46'), price: 350 },
  { type: t('pg.SocialRunClub.29fbb3'), price: 250 },
  { type: t('pg.SocialRunClub.0bac70'), price: 150 },
];

export const SocialRunClub = () => {
  useTranslation();
  return (
    <div data-testid="social-run-club-page">
      <SEO
        title={t('pg.SocialRunClub.736bff')}
        description={t('pg.SocialRunClub.e89f4b')}
      />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/5319373/pexels-photo-5319373.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt={t('pg.SocialRunClub.0b6c73')}
            loading="eager"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <p className="text-green-400 font-medium uppercase tracking-widest mb-4 flex items-center gap-2">
            <Footprints className="w-4 h-4" /> {t('pg.SocialRunClub.ff5a82')}
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-white mb-6">
            {t('pg.SocialRunClub.e16b95')} <br />
            <span className="text-green-400">{t('pg.SocialRunClub.cfd9ae')}</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mb-8">
            {t('pg.SocialRunClub.923cf3')}
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="run-club-join-btn"
            >
              <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-green-600 shadow-lg">
                {t('pg.SocialRunClub.8df769')}
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href="#hvordan-melde-seg-inn">
              <Button variant="outline" className="border-white/30 text-white uppercase tracking-wider px-8 py-6 text-lg hover:bg-white/10">
                {t('pg.SocialRunClub.664683')}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Intro – Hva er Social Run Club */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.SocialRunClub.1a6e2c')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-6">
              {t('pg.SocialRunClub.af70d5')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-lg max-w-3xl mx-auto leading-relaxed">
              {t('pg.SocialRunClub.5d82f3')}{' '}
              <strong className="text-green-500">{t('pg.SocialRunClub.885d9f')}</strong>
            </p>
          </div>

          <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Footprints className="w-6 h-6 text-green-500" aria-hidden="true" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                {t('pg.SocialRunClub.871825')}
              </h3>
            </div>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
              {t('pg.SocialRunClub.b74b7e')} <strong>{t('pg.SocialRunClub.336b2c')}</strong>.
            </p>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
              {t('pg.SocialRunClub.a2bad4')}
            </p>
          </div>
        </div>
      </section>

      {/* Why join */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.SocialRunClub.1771db')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
              {t('pg.SocialRunClub.c08a86')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {getbenefits().map((benefit, i) => (
              <div
                key={i}
                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 flex items-start gap-3"
              >
                <div className="w-9 h-9 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-green-500" aria-hidden="true" />
                </div>
                <p className="text-slate-700 dark:text-slate-200 font-medium pt-1">{benefit}</p>
              </div>
            ))}
          </div>

          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 sm:p-8 text-center">
            <Sparkles className="w-8 h-8 text-green-500 mx-auto mb-3" aria-hidden="true" />
            <p className="text-slate-900 dark:text-white text-lg font-medium leading-relaxed">
              {t('pg.SocialRunClub.39152f')}
              <br />
              <span className="text-green-500 font-bold">
                {t('pg.SocialRunClub.b1fe43')}
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* For hvem */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/30 rounded-2xl p-8 sm:p-12">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="w-14 h-14 bg-green-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Users className="w-7 h-7 text-white" aria-hidden="true" />
              </div>
              <div>
                <p className="text-green-500 font-medium uppercase tracking-widest mb-2">{t('pg.SocialRunClub.237ea4')}</p>
                <h2 className="text-3xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
                  {t('pg.SocialRunClub.50a11a')}
                </h2>
                <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed mb-4">
                  {t('pg.SocialRunClub.c2a5c5')}
                </p>
                <p className="text-slate-700 dark:text-slate-200 font-bold text-lg">
                  {t('pg.SocialRunClub.7501dd')}
                </p>
                <p className="text-green-500 font-medium mt-2 flex items-center gap-2">
                  <Heart className="w-5 h-5" aria-hidden="true" /> {t('pg.SocialRunClub.876f41')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Slik melder du deg inn */}
      <section id="hvordan-melde-seg-inn" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50 dark:bg-slate-900 scroll-mt-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.SocialRunClub.6831ce')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
              {t('pg.SocialRunClub.2eb5b0')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              {t('pg.SocialRunClub.1f793b')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {getageGroups().map((group) => {
              const Icon = group.icon;
              return (
                <a
                  key={group.label}
                  href={group.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`run-club-signup-${group.label.toLowerCase()}`}
                  className="group bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 hover:border-green-500 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500 transition-colors">
                    <Icon className="w-6 h-6 text-green-500 group-hover:text-white transition-colors" aria-hidden="true" />
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-sm uppercase tracking-wider mb-1">
                    {group.age}
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
                    {group.label}
                  </h3>
                  <span className="inline-flex items-center gap-2 text-green-500 font-medium text-sm">
                    {t('pg.SocialRunClub.fdb085')}
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </a>
              );
            })}
          </div>

          {/* Steps */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6 flex items-center gap-3">
              <Smartphone className="w-5 h-5 text-green-500" aria-hidden="true" />
              {t('pg.SocialRunClub.f9e0af')}
            </h3>
            <ol className="space-y-4">
              {[
                t('pg.SocialRunClub.667c04'),
                t('pg.SocialRunClub.0e5c38'),
                t('pg.SocialRunClub.b916a9'),
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-500 text-white font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-slate-700 dark:text-slate-200 pt-1">{step}</p>
                </li>
              ))}
            </ol>
            <div className="mt-6 bg-slate-100 dark:bg-slate-900 rounded-lg p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-slate-600 dark:text-slate-300 text-sm">
                <strong>{t('pg.SocialRunClub.f67dcb')}</strong> {t('pg.SocialRunClub.554daf')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Priser */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-green-500 font-medium uppercase tracking-widest mb-4">{t('pg.SocialRunClub.a7260e')}</p>
            <h2 className="text-4xl font-bold tracking-tighter text-slate-900 dark:text-white mb-4">
              {t('pg.SocialRunClub.d5fa92')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              {t('pg.SocialRunClub.181f5d')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Medlemskontingent */}
            <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="bg-green-500/10 p-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t('pg.SocialRunClub.073bec')}</h3>
                </div>
              </div>
              <div className="p-4">
                <table className="w-full">
                  <tbody>
                    {getmembershipPrices().map((item, i) => (
                      <tr key={i} className={i !== getmembershipPrices().length - 1 ? 'border-b border-slate-200 dark:border-slate-700' : ''}>
                        <td className="py-3 text-slate-600 dark:text-slate-300">{item.type}</td>
                        <td className="py-3 text-right text-green-500 font-bold text-lg">{item.price} kr</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Treningsavgift */}
            <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="bg-green-500/10 p-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <Footprints className="w-5 h-5 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t('pg.SocialRunClub.f5e43a')}</h3>
                </div>
              </div>
              <div className="p-4">
                <table className="w-full">
                  <tbody>
                    {gettrainingPrices().map((item, i) => (
                      <tr key={i} className={i !== gettrainingPrices().length - 1 ? 'border-b border-slate-200 dark:border-slate-700' : ''}>
                        <td className="py-3 text-slate-600 dark:text-slate-300">{item.type}</td>
                        <td className="py-3 text-right text-green-500 font-bold text-lg">{item.price} kr</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold tracking-tighter text-white mb-4">
            {t('pg.SocialRunClub.2e06d2')}
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            {t('pg.SocialRunClub.ccc3fd')}
          </p>
          <a
            href="https://app.hoopit.io/join/veitvet-sportsklubb-532465/open?skipWelcome=true&push=true"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button className="bg-green-500 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg hover:bg-green-600 shadow-lg">
              {t('pg.SocialRunClub.3a845a')}
              <ExternalLink className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default SocialRunClub;
